import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box, Button, Chip, Grid, LinearProgress, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Alert } from '@mui/material';
import { api } from '../api/client';
import type { ActivityRow, AssetSummary, FeePayerStatus, ReconResult } from '../api/types';
import { PageHeader, fmtUnits, AddressWithCopy, shortAddr, TxHashLink } from '../components/common';

function Row({ k, v }: { k: string; v: ReactNode }) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography color="text.secondary">{k}</Typography>
      <Typography fontWeight={700}>{v}</Typography>
    </Stack>
  );
}

export function AdminMonitoring() {
  const qc = useQueryClient();
  const assets = useQuery({ queryKey: ['mon-assets'], queryFn: () => api.get<AssetSummary>('/monitoring/assets') });
  const recon = useQuery({ queryKey: ['recon-results'], queryFn: () => api.get<ReconResult[]>('/recon/results') });
  const feePayer = useQuery({ queryKey: ['fee-payer'], queryFn: () => api.get<FeePayerStatus>('/admin/fee-payer') });
  const run = useMutation({
    mutationFn: () => api.post('/recon/run'),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['recon-results'] }),
  });

  const latest = recon.data?.length ? recon.data[0].round : 0;
  const rows = (recon.data ?? []).filter((r) => r.round === latest);

  return (
    <Box>
      <PageHeader crumb="관리자 · 정합성" title="정합성 · 모니터링"
        desc="10분 주기 4종 대사 결과와 자산 현황입니다."
        action={<Button variant="contained" disabled={run.isPending} onClick={() => run.mutate()}>정합성 배치 실행</Button>} />

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h2" mb={0.5}>서비스 계정 (가스)</Typography>
            <Typography variant="caption" color="text.secondary">
              대납자는 사용자 트랜잭션의 수수료만 부담하고, 운영(agent) 키는 발행·배포·정산·배당을 실행합니다.
              권한 등급이 다르므로 분리 운영합니다.
            </Typography>

            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap mt={2}>
              <Typography variant="body2" fontWeight={700}>수수료 대납자</Typography>
              <Stack alignItems="flex-end" spacing={0.5}>
                {feePayer.data && <AddressWithCopy address={feePayer.data.address} />}
                <Typography fontWeight={700}>
                  잔액 {feePayer.data ? Number(feePayer.data.balanceMeta).toLocaleString(undefined, { maximumFractionDigits: 4 }) : '—'} META
                </Typography>
              </Stack>
            </Stack>

            {feePayer.data?.operator && (
              <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap mt={1.5}>
                <Typography variant="body2" fontWeight={700}>운영(agent) 키</Typography>
                <Stack alignItems="flex-end" spacing={0.5}>
                  <AddressWithCopy address={feePayer.data.operator.address} />
                  <Typography fontWeight={700}>
                    잔액 {Number(feePayer.data.operator.balanceMeta).toLocaleString(undefined, { maximumFractionDigits: 4 })} META
                  </Typography>
                </Stack>
              </Stack>
            )}

            {feePayer.data?.low && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                대납자 잔액이 임계값({feePayer.data.thresholdMeta} META) 미만입니다. META를 충전하세요 — 부족 시 사용자 청약/전송이 실패합니다.
              </Alert>
            )}
            {feePayer.data?.operator?.low && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                운영(agent) 키 잔액이 임계값 미만입니다 — 발행·배포·정산·배당이 실패할 수 있습니다.
              </Alert>
            )}
            {feePayer.data?.separated === false && (
              <Alert severity="info" sx={{ mt: 2 }}>
                대납자와 운영(agent) 키가 <b>동일 계정</b>입니다. 최소권한·가용성 분리를 위해
                <code> FEE_PAYER_ADDRESS/FEE_PAYER_KEY_ID </code>를 별도 계정으로 주입하세요(운영매뉴얼 §5.1).
              </Alert>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h2" mb={2}>자산 현황</Typography>
            {assets.isLoading && <LinearProgress />}
            <Stack spacing={2}>
              {/* 스테이블코인(KRWS) */}
              {assets.data?.stablecoin && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>스테이블코인</Typography>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography>{assets.data.stablecoin.symbol} <Chip size="small" label="SC" sx={{ ml: 1 }} /></Typography>
                    <Typography fontWeight={700}>총발행 {fmtUnits(assets.data.stablecoin.totalSupply)}</Typography>
                  </Stack>
                </Box>
              )}

              {/* 토큰증권(있을 때) */}
              {(assets.data?.tokens ?? []).length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>토큰증권</Typography>
                  {(assets.data?.tokens ?? []).map((t) => (
                    <Stack key={t.contract} direction="row" justifyContent="space-between">
                      <Typography>{t.symbol} <Chip size="small" label={t.kind} sx={{ ml: 1 }} /></Typography>
                      <Typography fontWeight={700}>유통 {fmtUnits(t.circulating)}</Typography>
                    </Stack>
                  ))}
                </Box>
              )}

              {/* 청약/에스크로 */}
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>청약 · 에스크로</Typography>
                <Row k="청약 공고 (모집중/전체)" v={`${assets.data?.subscriptions.open ?? 0} / ${assets.data?.subscriptions.total ?? 0}`} />
                <Row k="청약 신청 (APPLIED)" v={`${assets.data?.subscriptions.applications ?? 0}건`} />
                {assets.data?.escrow && (
                  <Row k="에스크로 예치 총액" v={`${fmtUnits(assets.data.escrow.totalDeposited)} KRWS`} />
                )}
              </Box>

              {/* 계정 */}
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>계정</Typography>
                <Row k="Registry 등록 지갑(활성)" v={`${assets.data?.registeredWallets ?? 0}${assets.data?.registeredWalletsTotal != null && assets.data.registeredWalletsTotal !== assets.data.registeredWallets ? ` · 누계 ${assets.data.registeredWalletsTotal}(교체·폐기 포함)` : ''}`} />
              </Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h2" mb={2}>정합성 대사 (round {latest || '-'})</Typography>
            {recon.isLoading && <LinearProgress />}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>항목</TableCell>
                  <TableCell>온체인</TableCell>
                  <TableCell>DB</TableCell>
                  <TableCell>결과</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.item}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{r.onchainVal}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{r.dbVal}</TableCell>
                    <TableCell>
                      {/* 검증하지 않은 항목(대상 없음)을 '일치'로 표시하면 통과 신호가 왜곡된다 */}
                      {r.matched === null ? (
                        <Chip size="small" label="대상 없음" sx={{ fontWeight: 700 }} />
                      ) : (
                        <Chip size="small" color={r.matched ? 'success' : 'error'}
                          label={r.matched ? '일치' : '불일치'} sx={{ fontWeight: 700 }} />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 3 }}>실행 이력이 없습니다. [정합성 배치 실행]을 눌러주세요.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <ActivityTimeline />
        </Grid>
      </Grid>
    </Box>
  );
}

/**
 * 행위 타임라인 (SFR-006, A-9).
 *
 * 온체인 이벤트만으로는 "누가 시켰는지"를 알 수 없어서, 요청 이력과 txHash 로 이어 붙여
 * **행위자까지** 함께 보여준다. 체인에 이벤트를 남기지 않는 관리 행위(권한 변경·키 교체·배포)도
 * 요청 이력 쪽에서 같은 타임라인에 들어온다.
 */
function ActivityTimeline() {
  const { data, isLoading } = useQuery({
    queryKey: ['activity'],
    queryFn: () => api.get<ActivityRow[]>('/monitoring/activity?limit=300'),
    refetchInterval: 30000,
  });
  const all = data ?? [];
  // 필터: 행위 유형 · 행위자 · 대상(주소·내용) · 기간 — 최근 300건 안에서 거른다.
  const [kind, setKind] = useState('');
  const [actor, setActor] = useState('');
  const [target, setTarget] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const kinds = useMemo(() => [...new Set(all.map((r) => r.kind))].sort(), [all]);
  const rows = useMemo(() => {
    const a = actor.trim().toLowerCase();
    const t = target.trim().toLowerCase();
    const fromMs = from ? new Date(`${from}T00:00:00`).getTime() : null;
    const toMs = to ? new Date(`${to}T23:59:59.999`).getTime() : null;
    return all.filter((r) => {
      if (kind && r.kind !== kind) return false;
      if (a && !(r.actor ?? '').toLowerCase().includes(a)) return false;
      if (t && !`${r.contract ?? ''} ${JSON.stringify(r.params)} ${r.txHash ?? ''}`.toLowerCase().includes(t)) return false;
      if (fromMs !== null || toMs !== null) {
        if (!r.at) return false;
        const at = new Date(r.at).getTime();
        if (fromMs !== null && at < fromMs) return false;
        if (toMs !== null && at > toMs) return false;
      }
      return true;
    });
  }, [all, kind, actor, target, from, to]);
  const filtered = !!(kind || actor || target || from || to);

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h2" mb={0.5}>행위 타임라인</Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        발행·소각·동결·강제이전·배당 등 통제 행위의 감사 기록입니다. 온체인 이벤트와 요청 이력을 함께 봅니다.
      </Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} mb={2} alignItems={{ md: 'center' }}>
        <TextField select size="small" label="행위 유형" value={kind} onChange={(e) => setKind(e.target.value)} sx={{ minWidth: 180 }}>
          <MenuItem value="">전체</MenuItem>
          {kinds.map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}
        </TextField>
        <TextField size="small" label="행위자(계정)" value={actor} onChange={(e) => setActor(e.target.value)} />
        <TextField size="small" label="대상(주소·내용·txHash)" value={target} onChange={(e) => setTarget(e.target.value)} sx={{ minWidth: 220 }} />
        <TextField size="small" type="date" label="시작일" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label="종료일" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        {filtered && (
          <Button size="small" onClick={() => { setKind(''); setActor(''); setTarget(''); setFrom(''); setTo(''); }}>초기화</Button>
        )}
        <Typography variant="body2" color="text.secondary">{rows.length} / {all.length}건</Typography>
      </Stack>
      {isLoading && <LinearProgress />}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>시각</TableCell><TableCell>행위</TableCell><TableCell>행위자</TableCell>
            <TableCell>대상 · 내용</TableCell><TableCell>상태</TableCell><TableCell>트랜잭션</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={`${r.txHash ?? 'x'}-${r.kind}-${i}`} hover>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {r.at ? new Date(r.at).toLocaleString('ko-KR') : `#${r.blockNumber ?? '—'}`}
              </TableCell>
              <TableCell><Chip size="small" label={r.kind} sx={{ fontWeight: 700 }} /></TableCell>
              <TableCell>{r.actor ?? <span style={{ color: '#8a94a6' }}>—</span>}</TableCell>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, maxWidth: 380, wordBreak: 'break-all' }}>
                {r.contract && <div>{shortAddr(r.contract)}</div>}
                {JSON.stringify(r.params)}
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  color={r.status === 'FAILED' ? 'error' : r.status === 'ONCHAIN' || r.status === 'COMPLETED' ? 'success' : 'warning'}
                  label={r.status === 'ONCHAIN' ? '온체인 확정' : r.status}
                  sx={{ fontWeight: 700 }}
                />
              </TableCell>
              <TableCell><TxHashLink hash={r.txHash} /></TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && !isLoading && (
            <TableRow><TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 3 }}>
              {filtered ? '조건에 맞는 행위가 없습니다.' : '기록된 통제 행위가 없습니다.'}
            </TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );
}
